interface Portrait {
  cookingFrequency: string;
  familySize: number;
  peakHours: string;
  cuisine: string;
  habits: string[];
  appliances: string[];
  insurance: boolean;
  safetyCheck: string;
}

interface UserPortraitProps {
  portrait: Portrait;
  userName: string;
  yearlyUsage: number;
}

const dimensionData = [
  { label: "用气频率", key: "cookingFrequency", icon: "ri-time-line", color: "text-orange-500" },
  { label: "家庭规模", key: "familySize", icon: "ri-group-line", color: "text-teal-500" },
  { label: "用气高峰", key: "peakHours", icon: "ri-bar-chart-line", color: "text-green-500" },
  { label: "饮食偏好", key: "cuisine", icon: "ri-restaurant-line", color: "text-amber-500" },
];

// Radar chart dimensions
const radarDimensions = [
  { label: "用气规律性", value: 85 },
  { label: "安全合规性", value: 92 },
  { label: "缴费及时性", value: 78 },
  { label: "设备维护", value: 88 },
  { label: "用气经济性", value: 72 },
  { label: "保险意识", value: 95 },
];

export default function UserPortrait({ portrait, userName, yearlyUsage }: UserPortraitProps) {
  // Radar chart SVG
  const n = radarDimensions.length;
  const cx = 100;
  const cy = 100;
  const maxR = 70;

  const angleStep = (2 * Math.PI) / n;
  const getPoint = (i: number, r: number) => ({
    x: cx + r * Math.sin(i * angleStep),
    y: cy - r * Math.cos(i * angleStep),
  });

  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];
  const dataPoints = radarDimensions.map((d, i) => getPoint(i, (d.value / 100) * maxR));
  const dataPath = dataPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ") + " Z";

  const usageLevel = yearlyUsage < 200 ? "低用量" : yearlyUsage < 400 ? "中等用量" : "高用量";
  const usageLevelColor = yearlyUsage < 200 ? "text-green-600 bg-green-50" : yearlyUsage < 400 ? "text-amber-600 bg-amber-50" : "text-red-600 bg-red-50";

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-800">用户脸谱画像</h3>
        <button className="text-xs text-[#E8531A] border border-[#E8531A]/30 px-2.5 py-1 rounded-lg hover:bg-orange-50 cursor-pointer whitespace-nowrap">
          <i className="ri-robot-2-line mr-1"></i>AI生成画像报告
        </button>
      </div>

      <div className="flex gap-5">
        {/* Radar Chart */}
        <div className="flex-shrink-0">
          <svg width="200" height="200" viewBox="0 0 200 200">
            {/* Grid */}
            {gridLevels.map((level, li) => {
              const pts = Array.from({ length: n }, (_, i) => getPoint(i, level * maxR));
              const path = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ") + " Z";
              return <path key={li} d={path} fill="none" stroke="#f3f4f6" strokeWidth="1" />;
            })}
            {/* Axes */}
            {Array.from({ length: n }, (_, i) => {
              const p = getPoint(i, maxR);
              return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#e5e7eb" strokeWidth="1" />;
            })}
            {/* Data area */}
            <path d={dataPath} fill="#E8531A" fillOpacity="0.15" stroke="#E8531A" strokeWidth="2" />
            {/* Data points */}
            {dataPoints.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r="3" fill="#E8531A" />
            ))}
            {/* Labels */}
            {radarDimensions.map((d, i) => {
              const p = getPoint(i, maxR + 18);
              return (
                <text key={i} x={p.x} y={p.y} textAnchor="middle" dominantBaseline="middle" fontSize="9" fill="#6b7280">
                  {d.label}
                </text>
              );
            })}
          </svg>
        </div>

        {/* Portrait info */}
        <div className="flex-1 space-y-3">
          {/* User type tag */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${usageLevelColor}`}>{usageLevel}用户</span>
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${portrait.safetyCheck === "按时" ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>
              安检{portrait.safetyCheck}
            </span>
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${portrait.insurance ? "bg-teal-50 text-teal-600" : "bg-gray-100 text-gray-500"}`}>
              {portrait.insurance ? "已投保" : "未投保"}
            </span>
          </div>

          {/* Dimension info */}
          <div className="grid grid-cols-2 gap-2">
            {dimensionData.map((dim) => (
              <div key={dim.key} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2.5">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className={`${dim.icon} ${dim.color} text-sm`}></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-400">{dim.label}</p>
                  <p className="text-xs font-medium text-gray-700 truncate">
                    {dim.key === "familySize"
                      ? `${portrait[dim.key as keyof Portrait]} 人`
                      : String(portrait[dim.key as keyof Portrait])}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Habits */}
          <div>
            <p className="text-xs text-gray-400 mb-1.5">用气习惯</p>
            <div className="flex flex-wrap gap-1.5">
              {portrait.habits.map((h, i) => (
                <span key={i} className="text-xs px-2 py-0.5 bg-orange-50 text-[#E8531A] rounded-full">{h}</span>
              ))}
            </div>
          </div>

          {/* Appliances */}
          <div>
            <p className="text-xs text-gray-400 mb-1.5">燃气器具</p>
            <div className="flex flex-wrap gap-1.5">
              {portrait.appliances.map((a, i) => (
                <span key={i} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{a}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* AI insight */}
      <div className="mt-4 pt-4 border-t border-gray-50">
        <div className="flex items-start gap-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4">
          <div className="w-8 h-8 rounded-lg bg-[#E8531A] flex items-center justify-center flex-shrink-0">
            <i className="ri-robot-2-line text-white text-sm"></i>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-800 mb-1">AI智能分析</p>
            <p className="text-xs text-gray-600 leading-relaxed">
              {userName} 为典型的<strong>家庭型高频用气用户</strong>，家庭人口较多，三餐均有用气习惯，冬季因壁挂炉取暖用气量显著增加。
              用气规律性强，安全合规意识良好，建议在冬季来临前提前进行安全检查，并关注壁挂炉的定期维护保养。
              根据历史数据预测，未来三个月用气量将持续增加，建议提前充值备用。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
